import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBrand } from '../../app/config/brand';
import { useCases } from './hooks/useCases';
import { CaseStatsStrip } from './components/CaseStatsStrip';
import { CaseFilterBar } from './components/CaseFilterBar';
import { CasesPagination } from './components/CasesPagination';
import type { CaseListItem, CaseListStatus } from '@lds-efsp/shared-contracts';

type FilterKey = CaseListStatus | 'all' | 'recentlyFiled';
type ViewMode = 'list' | 'table';
type SortKey = 'lastActivity' | 'caseTitle' | 'createdAt';

const STATUS_CONFIG: Record<CaseListStatus, { label: string; colorKey: string }> = {
  active: { label: 'Active', colorKey: 'success' },
  pending: { label: 'Pending', colorKey: 'info' },
  needs_attention: { label: 'Needs Attention', colorKey: 'warning' },
  closed: { label: 'Closed', colorKey: 'textMuted' },
};

const SORT_LABELS: Record<SortKey, string> = {
  lastActivity: 'Most recent activity',
  caseTitle: 'Case name',
  createdAt: 'Date created',
};

const CALIFORNIA_COUNTIES = [
  'Alameda', 'Alpine', 'Amador', 'Butte', 'Calaveras', 'Colusa', 'Contra Costa',
  'Del Norte', 'El Dorado', 'Fresno', 'Glenn', 'Humboldt', 'Imperial', 'Inyo',
  'Kern', 'Kings', 'Lake', 'Lassen', 'Los Angeles', 'Madera', 'Marin', 'Mariposa',
  'Mendocino', 'Merced', 'Modoc', 'Mono', 'Monterey', 'Napa', 'Nevada', 'Orange',
  'Placer', 'Plumas', 'Riverside', 'Sacramento', 'San Benito', 'San Bernardino',
  'San Diego', 'San Francisco', 'San Joaquin', 'San Luis Obispo', 'San Mateo',
  'Santa Barbara', 'Santa Clara', 'Santa Cruz', 'Shasta', 'Sierra', 'Siskiyou',
  'Solano', 'Sonoma', 'Stanislaus', 'Sutter', 'Tehama', 'Trinity', 'Tulare',
  'Tuolumne', 'Ventura', 'Yolo', 'Yuba',
];

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const diff = now - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

interface StatusChipProps {
  status: CaseListStatus;
}

const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const brand = useBrand();
  const colors = brand.colors as Record<string, string>;
  const cfg = STATUS_CONFIG[status];
  const color = colors[cfg.colorKey] ?? colors.textMuted;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {cfg.label}
    </span>
  );
};

interface CaseListRowProps {
  item: CaseListItem;
  onNavigate: (caseNumber: string) => void;
}

const CaseListRow: React.FC<CaseListRowProps> = ({ item, onNavigate }) => {
  const brand = useBrand();
  const isAttention = item.status === 'needs_attention';
  const isClosed = item.status === 'closed';

  return (
    <div
      className="flex items-center gap-4 px-5 py-4 transition-colors cursor-pointer"
      style={{
        backgroundColor: isAttention ? `${brand.colors.warning}08` : brand.colors.bgCard,
        borderBottom: `1px solid ${brand.colors.border}`,
        opacity: isClosed ? 0.7 : 1,
      }}
      onClick={() => onNavigate(item.caseNumber)}
      data-testid="case-row"
    >
      <div className="w-36 shrink-0">
        <StatusChip status={item.status} />
      </div>

      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: brand.colors.secondary }}
        >
          {item.caseTitle ?? 'Untitled Case'}
        </div>
        <div className="flex items-center gap-1.5 text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
          <span className="font-mono">{item.caseNumber}</span>
          {item.caseCategory && (
            <>
              <span style={{ color: brand.colors.textMuted }}>·</span>
              <span>{item.caseCategory}</span>
            </>
          )}
        </div>
        {isAttention && (
          <div
            className="flex items-center gap-1 text-xs font-medium mt-1"
            style={{ color: brand.colors.warning }}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Court response due
          </div>
        )}
      </div>

      <div className="w-40 shrink-0 hidden lg:block">
        <div className="text-sm" style={{ color: brand.colors.text }}>
          {item.county ?? '-'}
        </div>
        <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
          {item.courtLocation ?? '-'}
        </div>
      </div>

      <div className="w-32 shrink-0 hidden md:block text-right">
        <div className="text-sm" style={{ color: brand.colors.text }}>
          {formatDate(item.lastActivity.date)}
        </div>
        <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
          {isClosed ? 'Read-only' : formatRelativeTime(item.lastActivity.date)}
        </div>
      </div>

      <div className="w-36 shrink-0 flex items-center justify-end gap-2">
        {isClosed ? (
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{
              border: `1px solid ${brand.colors.border}`,
              color: brand.colors.textLight,
              backgroundColor: brand.colors.bgCard,
            }}
            onClick={(e) => { e.stopPropagation(); onNavigate(item.caseNumber); }}
          >
            View
          </button>
        ) : (
          <button
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            onClick={(e) => { e.stopPropagation(); onNavigate(item.caseNumber); }}
          >
            + Place Order
          </button>
        )}
      </div>
    </div>
  );
};

interface CaseCompactTableProps {
  cases: CaseListItem[];
  onNavigate: (caseNumber: string) => void;
}

const CaseCompactTable: React.FC<CaseCompactTableProps> = ({ cases, onNavigate }) => {
  const brand = useBrand();

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
    >
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgLight }}>
            <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Status</th>
            <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Case</th>
            <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden lg:table-cell" style={{ color: brand.colors.textLight }}>County</th>
            <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden lg:table-cell" style={{ color: brand.colors.textLight }}>Jurisdiction</th>
            <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider hidden md:table-cell" style={{ color: brand.colors.textLight }}>Last Activity</th>
            <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: brand.colors.textLight }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((item) => {
            const isClosed = item.status === 'closed';
            const isAttention = item.status === 'needs_attention';
            return (
              <tr
                key={item.caseNumber}
                className="cursor-pointer transition-colors"
                style={{
                  borderBottom: `1px solid ${brand.colors.border}`,
                  opacity: isClosed ? 0.7 : 1,
                  backgroundColor: isAttention ? `${brand.colors.warning}08` : undefined,
                }}
                onClick={() => onNavigate(item.caseNumber)}
                data-testid="case-table-row"
              >
                <td className="px-4 py-3">
                  <StatusChip status={item.status} />
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold truncate max-w-xs" style={{ color: brand.colors.secondary }}>
                    {item.caseTitle ?? 'Untitled Case'}
                  </div>
                  <div className="text-xs font-mono mt-0.5" style={{ color: brand.colors.textLight }}>
                    {item.caseNumber}
                  </div>
                  {isAttention && (
                    <div className="flex items-center gap-1 text-xs font-medium mt-1" style={{ color: brand.colors.warning }}>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Court response due
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell" style={{ color: brand.colors.text }}>
                  {item.county ?? '-'}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div style={{ color: brand.colors.text }}>{item.courtLocation ?? '-'}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div style={{ color: brand.colors.text }}>{formatDate(item.lastActivity.date)}</div>
                  <div className="text-xs mt-0.5" style={{ color: brand.colors.textLight }}>
                    {isClosed ? 'Read-only' : formatRelativeTime(item.lastActivity.date)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {isClosed ? (
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{
                        border: `1px solid ${brand.colors.border}`,
                        color: brand.colors.textLight,
                        backgroundColor: brand.colors.bgCard,
                      }}
                      onClick={(e) => { e.stopPropagation(); onNavigate(item.caseNumber); }}
                    >
                      View
                    </button>
                  ) : (
                    <button
                      className="px-3 py-1.5 rounded-lg text-xs font-medium"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                      onClick={(e) => { e.stopPropagation(); onNavigate(item.caseNumber); }}
                    >
                      + Place Order
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ListIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <path d="M2 4h12M2 8h12M2 12h12" />
  </svg>
);

const TableIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8">
    <rect x="2" y="3" width="12" height="10" rx="1" />
    <path d="M2 7h12M6 7v6" />
  </svg>
);

const CustomerCases: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const { cases, stats, pagination, isLoading, error, filters, setFilters, refetch } = useCases();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [county, setCounty] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('lastActivity');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const activeFilter: FilterKey = filters.recentlyFiled
    ? 'recentlyFiled'
    : (filters.status ?? 'all');

  const handleFilterChange = useCallback((filter: FilterKey) => {
    if (filter === 'all') {
      setFilters({ status: undefined, recentlyFiled: false });
    } else if (filter === 'recentlyFiled') {
      setFilters({ status: undefined, recentlyFiled: true });
    } else {
      setFilters({ status: filter, recentlyFiled: false });
    }
  }, [setFilters]);

  const handleSearchChange = useCallback((search: string) => {
    setFilters({ search: search || undefined });
  }, [setFilters]);

  const handlePageChange = useCallback((page: number) => {
    setFilters({ page });
  }, [setFilters]);

  const handleCountyChange = useCallback((value: string) => {
    setCounty(value);
    setFilters({ county: value || undefined } as Parameters<typeof setFilters>[0]);
  }, [setFilters]);

  const handleSortChange = useCallback((sort: SortKey) => {
    setSortKey(sort);
    setShowSortMenu(false);
    setFilters({ sort } as Parameters<typeof setFilters>[0]);
  }, [setFilters]);

  const handleNavigateToCase = useCallback((caseNumber: string) => {
    navigate(`/cases/${caseNumber}`);
  }, [navigate]);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1
            className="text-3xl font-light"
            style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
          >
            Your Cases
          </h1>
          <p className="mt-1 text-sm" style={{ color: brand.colors.textLight }}>
            {pagination.total} cases you've placed orders for. Pick a case to file a new document, view history, or order service.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex rounded-lg overflow-hidden"
            style={{ border: `1px solid ${brand.colors.border}` }}
            role="group"
            aria-label="View"
          >
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: viewMode === 'list' ? brand.colors.primary : brand.colors.bgCard,
                color: viewMode === 'list' ? brand.colors.onPrimary : brand.colors.textLight,
              }}
              onClick={() => setViewMode('list')}
            >
              <ListIcon color={viewMode === 'list' ? '#FFFFFF' : brand.colors.textLight} />
              List
            </button>
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                backgroundColor: viewMode === 'table' ? brand.colors.primary : brand.colors.bgCard,
                color: viewMode === 'table' ? brand.colors.onPrimary : brand.colors.textLight,
                borderLeft: `1px solid ${brand.colors.border}`,
              }}
              onClick={() => setViewMode('table')}
            >
              <TableIcon color={viewMode === 'table' ? '#FFFFFF' : brand.colors.textLight} />
              Compact
            </button>
          </div>
        </div>
      </div>

      <CaseStatsStrip stats={stats} isLoading={isLoading && !stats} />

      <CaseFilterBar
        activeFilter={activeFilter}
        search={filters.search ?? ''}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <select
            value={county}
            aria-label="County"
            onChange={(e) => handleCountyChange(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 rounded-lg text-sm"
            style={{
              backgroundColor: brand.colors.bgLight,
              border: `1px solid ${brand.colors.border}`,
              color: county ? brand.colors.text : brand.colors.textLight,
            }}
          >
            <option value="">All counties</option>
            {CALIFORNIA_COUNTIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <svg
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            fill="none"
            stroke={brand.colors.textMuted}
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm" style={{ color: brand.colors.textLight }}>
          <span>Showing </span>
          <strong style={{ color: brand.colors.text }}>
            {cases.length} of {pagination.total}
          </strong>
        </div>
        <div className="relative">
          <span className="text-sm mr-1" style={{ color: brand.colors.textLight }}>sorted by</span>
          <button
            className="text-sm font-medium inline-flex items-center gap-1"
            style={{ color: brand.colors.text }}
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            {SORT_LABELS[sortKey]}
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showSortMenu && (
            <div
              className="absolute right-0 top-full mt-1 rounded-lg shadow-lg py-1 z-10 min-w-48"
              style={{ backgroundColor: brand.colors.bgCard, border: `1px solid ${brand.colors.border}` }}
            >
              {(Object.entries(SORT_LABELS) as [SortKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className="w-full text-left px-4 py-2 text-sm transition-colors"
                  style={{
                    color: key === sortKey ? brand.colors.link : brand.colors.text,
                    fontWeight: key === sortKey ? 600 : 400,
                  }}
                  onClick={() => handleSortChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg p-4 mb-6 flex items-center justify-between"
          style={{ backgroundColor: '#FEE2E2', color: brand.colors.error }}
          data-testid="error-banner"
        >
          <span className="text-sm">{error}</span>
          <button
            onClick={refetch}
            className="text-sm font-medium underline ml-4"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3" data-testid="loading-rows">
          {[1, 2, 3, 4, 5, 6].map((key) => (
            <div
              key={key}
              className="h-16 rounded-xl animate-pulse"
              style={{ backgroundColor: brand.colors.bgLight }}
            />
          ))}
        </div>
      )}

      {!isLoading && !error && cases.length === 0 && (
        <div
          className="flex flex-col items-center justify-center py-16 rounded-xl"
          style={{ backgroundColor: brand.colors.bgLight }}
          data-testid="empty-state"
        >
          <svg className="w-12 h-12 mb-4" fill="none" stroke={brand.colors.textMuted} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p className="text-base font-medium mb-1" style={{ color: brand.colors.secondary }}>No cases found</p>
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            {filters.search || filters.status || filters.recentlyFiled || county
              ? 'Try adjusting your filters or search terms.'
              : 'Cases will appear here once you place your first order.'}
          </p>
        </div>
      )}

      {!isLoading && !error && cases.length > 0 && (
        <>
          {viewMode === 'list' ? (
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: `1px solid ${brand.colors.border}`, backgroundColor: brand.colors.bgCard }}
              data-testid="case-list"
            >
              {cases.map((c) => (
                <CaseListRow
                  key={c.caseNumber}
                  item={c}
                  onNavigate={handleNavigateToCase}
                />
              ))}
            </div>
          ) : (
            <CaseCompactTable cases={cases} onNavigate={handleNavigateToCase} />
          )}
          <CasesPagination
            page={pagination.page}
            perPage={pagination.perPage}
            total={pagination.total}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default CustomerCases;
